import React from 'react';
import { motion } from 'framer-motion';
import {
    FaPhone,
    FaEnvelope,
    FaWhatsapp,
    FaDownload,
    FaMapMarkerAlt,
    FaGlobe,
    FaShareAlt,
    FaInstagram
} from 'react-icons/fa';
import logo from '../assets/logo.png';

// Define props interface
export interface ContactInfo {
    name: string;
    title: string;
    phone: string;
    email: string;
    website: string;
    location: string;
    photoUrl?: string; // Optional URL for custom photo
    logoUrl?: string; // Optional URL for custom logo
    instagram?: string; // Optional Instagram URL
}

// Theme interface
export interface ThemeConfig {
    primaryColor: string;
    secondaryColor: string;
    gradientStart: string;
    gradientEnd: string;
    buttonTextColor: string;
    fontFamily?: string;
}

// Default EasyBuy Theme
const defaultTheme: ThemeConfig = {
    primaryColor: '#FBA520', // Orange-500
    secondaryColor: '#FFF7ED', // Orange-50
    gradientStart: '#FB923C', // Orange-400
    gradientEnd: '#FBA520', // Orange-500
    buttonTextColor: '#EA580C', // Orange-600
};

interface VisitingCardProps {
    contactInfo?: ContactInfo;
    theme?: ThemeConfig;
}

const VisitingCardPage: React.FC<VisitingCardProps> = ({
    contactInfo = {
        name: 'Nashwan',
        title: 'EasyBuy General Trading',
        phone: '',
        email: '',
        website: 'https://easybuytrd.com',
        location: 'Dubai, UAE'
    },
    theme = defaultTheme
}) => {

    const generateVCard = () => {
        const vcard = `BEGIN:VCARD
VERSION:3.0
N:;${contactInfo.name};;;
FN:${contactInfo.name}
ORG:${contactInfo.title}
TEL;TYPE=CELL:${contactInfo.phone}
EMAIL:${contactInfo.email}
URL:${contactInfo.website}
ADR;TYPE=WORK:;;${contactInfo.location}
END:VCARD`;

        const blob = new Blob([vcard], { type: 'text/vcard' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${contactInfo.name}.vcf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: `${contactInfo.name} - Digital Card`,
                    text: `Check out ${contactInfo.name}'s business card`,
                    url: window.location.href,
                });
            } catch (err) {
                console.error('Error sharing:', err);
            }
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="w-full max-w-sm bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden relative"
                style={{ fontFamily: theme.fontFamily }}
            >
                {/* Top Decoration */}
                <div
                    className="h-2 w-full"
                    style={{ background: `linear-gradient(to right, ${theme.gradientStart}, ${theme.gradientEnd})` }}
                />

                <div className="px-8 pt-8 pb-8 flex flex-col items-center text-center">
                    {/* Company Logo */}
                    <div className="mb-4 w-48">
                        <img
                            src={contactInfo.logoUrl || logo}
                            alt={contactInfo.title}
                            className="w-full h-auto object-contain"
                        />
                    </div>

                    {/* Profile Photo */}
                    <div className="mb-6 relative">
                        <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-white shadow-lg mx-auto bg-gray-100">
                            {/* Replace 'https://ui-avatars.com/...' with your actual photo path, e.g., import photo from '../assets/photo.jpg' */}
                            <img
                                src={contactInfo.photoUrl || `https://ui-avatars.com/api/?name=${contactInfo.name}&background=${theme.primaryColor.replace('#', '')}&color=fff&size=256`}
                                alt={contactInfo.name}
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>

                    {/* Profile Section */}
                    <div className="mb-8">
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">{contactInfo.name}</h1>
                        <p className="text-gray-500 font-medium">{contactInfo.title}</p>
                    </div>

                    {/* Primary Actions */}
                    <div className="flex gap-4 mb-10 w-full justify-center flex-wrap">
                        <ActionButton href={`tel:${contactInfo.phone}`} icon={<FaPhone />} label="Call" theme={theme} />
                        <ActionButton href={`mailto:${contactInfo.email}`} icon={<FaEnvelope />} label="Email" theme={theme} />
                        <ActionButton href={`https://wa.me/${contactInfo.phone.replace(/[^0-9]/g, '')}`} icon={<FaWhatsapp />} label="WhatsApp" theme={theme} />
                        {contactInfo.instagram && (
                            <ActionButton href={contactInfo.instagram} icon={<FaInstagram />} label="Instagram" theme={theme} />
                        )}
                    </div>

                    {/* Info Section */}
                    <div className="w-full space-y-4 mb-4">
                        <InfoRow icon={<FaGlobe />} text="Website" href={contactInfo.website} theme={theme} />
                        <InfoRow icon={<FaMapMarkerAlt />} text={contactInfo.location} theme={theme} />
                    </div>

                    {/* Secondary Actions */}
                    <div className="flex gap-3 w-full mt-6">
                        <button
                            onClick={generateVCard}
                            className="flex-1 py-3 px-4 text-white rounded-xl font-medium text-sm flex items-center justify-center gap-2 transition-colors shadow-lg shadow-gray-200 hover:opacity-90"
                            style={{ backgroundColor: theme.primaryColor }}
                        >
                            <FaDownload className="text-xs" />
                            Save Contact
                        </button>
                        <button
                            onClick={handleShare}
                            className="w-12 h-12 flex items-center justify-center bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 transition-colors"
                        >
                            <FaShareAlt />
                        </button>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

// Helper Components
const ActionButton = ({ href, icon, label, theme }: { href: string; icon: React.ReactNode; label: string; theme: ThemeConfig }) => (
    <a
        href={href}
        className={`flex flex-col items-center gap-2 group w-16`}
    >
        <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center text-xl transition-transform group-active:scale-95"
            style={{ backgroundColor: theme.secondaryColor, color: theme.primaryColor }}
        >
            {icon}
        </div>
        <span className="text-xs font-medium text-gray-500">{label}</span>
    </a>
);

const InfoRow = ({ icon, text, href, theme }: { icon: React.ReactNode; text: string; href?: string; theme: ThemeConfig }) => {
    const content = (
        <div className="flex items-center gap-4 py-3 px-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors border border-gray-100">
            <div className="text-lg" style={{ color: theme.primaryColor }}>{icon}</div>
            <span className="text-gray-600 font-medium text-sm truncate">{href ? text.replace(/^https?:\/\//, '') : text}</span>
        </div>
    );

    return href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" className="block w-full">
            {content}
        </a>
    ) : (
        <div className="w-full">{content}</div>
    );
};

export default VisitingCardPage;
